import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Accordion } from '@gerdau/hefesto-mais-web';
import {
  ResultContainer,
  ResultTitle,
  AccordionContainer,
  ResultSubtitle,
  ResultHeader,
  ResultAccordionTitle,
  Message,
  ContainerMessage
} from './styles';
import { HelpCenterAccordionItem } from '../HelpCenterAccordionItem';
import { HelpCenterAttachments } from '../HelpCenterAttachments';
import { ApplicationState } from '../../../store';
import { loadHelpCenterQuery } from '../../../store/ducks/help-center/actions';
import { GetFlag } from '../../../utils/featureFlag';
import { splitTopics } from '../helpCenterTopics';

export const HelpCenterQueryResult = () => {
  const { search } = useLocation();
  const dispatch = useDispatch();
  const isNewApiEnabled = GetFlag('help_center_new_api');
  const searchTerm = search ? decodeURIComponent(search.substring(1)) : '';

  const helpCenterStatus = useSelector(
    (state: ApplicationState) => state.helpCenter
  );
  const customersState = useSelector(
    (appState: ApplicationState) => appState.customers
  );
  const helpCenterLoading = useSelector(
    (state: ApplicationState) => state.helpCenter.loading
  );
  const customerLoading =
    customersState.loading ||
    !!customersState.loadingPurchaseReceivers ||
    !!customersState.loadingProductDescription ||
    !!customersState.loadingBlocks ||
    !!customersState.loadingCustomerById;

  const loading = helpCenterLoading || customerLoading;

  React.useEffect(() => {
    (() => {
      if (
        customersState.initialized &&
        !customerLoading &&
        searchTerm.length >= 3
      ) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        dispatch(loadHelpCenterQuery(searchTerm));
      }
    })();
  }, [customerLoading, search]);

  if (loading) {
    return null;
  }

  const emptyResult = (
    <ContainerMessage>
      <ResultHeader>
        <ResultTitle>Resultados da busca</ResultTitle>
        <ResultSubtitle>
          Este é o resultado da sua busca por <span>{searchTerm}</span>:
        </ResultSubtitle>
      </ResultHeader>
      <Message>
        Não foram encontrados <br />
        resultados para sua busca.
      </Message>
    </ContainerMessage>
  );

  if (isNewApiEnabled) {
    if (!searchTerm) {
      return null;
    }

    const { questions, manuals } = splitTopics(
      helpCenterStatus.commerce.searchResults
    );

    if (questions.length === 0 && manuals.length === 0) {
      return emptyResult;
    }

    return (
      <ResultContainer>
        <ResultHeader>
          <ResultTitle>Resultados da busca</ResultTitle>
          <ResultSubtitle>
            Este é o resultado da sua busca por <span>{searchTerm}</span>:
          </ResultSubtitle>
        </ResultHeader>

        {questions.length > 0 && (
          <AccordionContainer>
            <ResultAccordionTitle>Perguntas</ResultAccordionTitle>
            {questions.map((item) => (
              <Accordion
                key={item.id}
                data={[
                  {
                    id: String(item.id),
                    title: item.title,
                    content: (
                      <HelpCenterAccordionItem
                        content={item.description}
                        isHtml
                      />
                    )
                  }
                ]}
              />
            ))}
          </AccordionContainer>
        )}

        <HelpCenterAttachments manuals={manuals} />
      </ResultContainer>
    );
  }

  const results = helpCenterStatus.faq.query.results;

  if (!results || !searchTerm) {
    return null;
  }

  if (results.length === 0) {
    return emptyResult;
  }

  const categories = results
    .map((item) => item.categories)
    .reduce((acc, item) => [...acc, ...item], []);

  return (
    <ResultContainer key={JSON.stringify(results)}>
      <ResultHeader>
        <ResultTitle>Resultados da busca</ResultTitle>
        <ResultSubtitle>
          Este é o resultado da sua busca por <span>{searchTerm}</span>:
        </ResultSubtitle>
      </ResultHeader>

      {results.length > 0 ? (
        <AccordionContainer>
          <ResultAccordionTitle>Perguntas</ResultAccordionTitle>
          {results.map((item) => (
            <Accordion
              key={item.question}
              data={[
                {
                  id: item.question,
                  title: item.question,
                  content: <HelpCenterAccordionItem content={item.answer} />
                }
              ]}
            />
          ))}
        </AccordionContainer>
      ) : null}

      <HelpCenterAttachments categories={categories} />
    </ResultContainer>
  );
};
